// components/branches/config/appointment/SchedulingRulesSettings.jsx
import React from "react";
import NumberInput from "../../../common/NumberInput";

const SchedulingRulesSettings = ({ config, onChange, isEditMode }) => {
  const handleChange = (field, value) => {
    onChange({ ...config, [field]: value });
  };

  const handleWalkInsChange = (field, value) => {
    onChange({
      ...config,
      walkIns: { ...config.walkIns, [field]: value },
    });
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Scheduling Rules</h3>

      {/* Slot Configuration */}
      <div className="bg-gray-50 rounded-lg p-6 space-y-4">
        <h4 className="font-medium">Slot Configuration</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Slot Duration
            </label>
            {isEditMode ? (
              <div className="flex items-center gap-2">
                <NumberInput
                  value={config.defaultSlotDuration || 30}
                  onChange={(value) =>
                    handleChange("defaultSlotDuration", value)
                  }
                  min="15"
                  max="240"
                  step="15"
                  className="w-24 focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">minutes</span>
              </div>
            ) : (
              <p className="text-lg font-semibold">
                {config.defaultSlotDuration || 30} minutes
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buffer Between Appointments
            </label>
            {isEditMode ? (
              <div className="flex items-center gap-2">
                <NumberInput
                  value={config.bufferBetweenAppointments || 10}
                  onChange={(value) =>
                    handleChange("bufferBetweenAppointments", value)
                  }
                  min="0"
                  max="60"
                  step="5"
                  className="w-24 focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">minutes</span>
              </div>
            ) : (
              <p className="text-lg font-semibold">
                {config.bufferBetweenAppointments || 10} minutes
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Concurrent Appointments */}
      <div className="bg-blue-50 rounded-lg p-6 space-y-4">
        <h4 className="font-medium">Concurrent Bookings</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Concurrent Appointments
            </label>
            {isEditMode ? (
              <NumberInput
                value={config.maxConcurrentAppointments || 3}
                onChange={(value) =>
                  handleChange("maxConcurrentAppointments", value)
                }
                min="1"
                max="10"
                className="w-24 focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <p className="text-lg font-semibold">
                {config.maxConcurrentAppointments || 3}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Customers at the same time
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Per Time Slot
            </label>
            {isEditMode ? (
              <NumberInput
                value={config.maxAppointmentsPerSlot || 1}
                onChange={(value) =>
                  handleChange("maxAppointmentsPerSlot", value)
                }
                min="1"
                max="5"
                className="w-24 focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <p className="text-lg font-semibold">
                {config.maxAppointmentsPerSlot || 1}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Walk-ins */}
      <div className="bg-green-50 rounded-lg p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h4 className="font-medium">Walk-in Management</h4>
            <p className="text-sm text-gray-600 mt-1">
              Handle customers without appointments
            </p>
          </div>
          {isEditMode ? (
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.walkIns?.allowed || false}
                onChange={(e) =>
                  handleWalkInsChange("allowed", e.target.checked)
                }
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
            </label>
          ) : (
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                config.walkIns?.allowed
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {config.walkIns?.allowed ? "Allowed" : "Not Allowed"}
            </span>
          )}
        </div>

        {config.walkIns?.allowed && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reserved Slots Per Day for Walk-ins
              </label>
              {isEditMode ? (
                <NumberInput
                  value={config.walkIns?.reservedSlotsPerDay || 2}
                  onChange={(value) =>
                    handleWalkInsChange("reservedSlotsPerDay", value)
                  }
                  min="0"
                  max="10"
                  className="w-24 focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <p className="text-lg font-semibold">
                  {config.walkIns?.reservedSlotsPerDay || 2} slots
                </p>
              )}
            </div>

            {isEditMode && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority
                </label>
                <select
                  value={config.walkIns?.priority || "APPOINTMENT"}
                  onChange={(e) =>
                    handleWalkInsChange("priority", e.target.value)
                  }
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="WALK_IN">Walk-ins First</option>
                  <option value="APPOINTMENT">Appointments First</option>
                  <option value="EQUAL">Equal Priority</option>
                </select>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SchedulingRulesSettings;
